import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, CheckSquare, Contact, FileText, FolderClosed, Handshake, Home, Inbox, LifeBuoy, Plus, Search, Settings, User } from 'lucide-react';
import { Sheet } from '../../ui/Sheet.tsx';
import { Avatar, Spinner, wsColor } from '../../ui/primitives.tsx';
import { useSearch, useWorkspaces } from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import type { SearchResult } from '@unitly/contracts';

interface Item {
  id: string;
  group: string;
  title: string;
  sub?: string;
  icon: React.ReactNode;
  run: () => void;
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { orgId, base, openTask, openQuickAdd } = useOrgCtx();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const search = useSearch(orgId, q);
  const workspaces = useWorkspaces(orgId);

  useEffect(() => {
    if (open) setQ('');
  }, [open]);

  const items = useMemo<Item[]>(() => {
    const go = (path: string) => () => { navigate(path); onClose(); };
    const term = q.trim().toLowerCase();
    const actions: Item[] = [
      { id: 'new', group: 'Azioni', title: 'Nuova task', icon: <Plus size={18} />, run: () => { onClose(); openQuickAdd(); } },
      { id: 'home', group: 'Vai a', title: 'Home', icon: <Home size={18} />, run: go(base) },
      { id: 'tasks', group: 'Vai a', title: 'Le mie task', icon: <CheckSquare size={18} />, run: go(`${base}/tasks`) },
      { id: 'inbox', group: 'Vai a', title: 'Inbox', icon: <Inbox size={18} />, run: go(`${base}/inbox`) },
      { id: 'settings', group: 'Vai a', title: 'Impostazioni', icon: <Settings size={18} />, run: go(`${base}/settings`) },
    ].filter((a) => !term || a.title.toLowerCase().includes(term));
    if (term.length < 2) {
      const ws = (workspaces.data ?? []).slice(0, 6).map<Item>((w) => ({
        id: `ws-${w.id}`, group: 'Spazi', title: w.name, sub: w.key,
        icon: <span className="ws-dot" style={{ '--ws-color': wsColor(w.color), width: 10, height: 10 } as React.CSSProperties} />,
        run: go(`${base}/w/${w.key}`),
      }));
      return [...actions, ...ws];
    }
    const groupOf: Record<SearchResult['type'], string> = {
      task: 'Task', workspace: 'Spazi', member: 'Persone', page: 'Documenti',
      crm_company: 'CRM', crm_contact: 'CRM', deal: 'CRM', ticket: 'Ticket',
    };
    const results = (search.data ?? []).map<Item>((r: SearchResult) => ({
      id: `${r.type}-${r.id}`,
      group: groupOf[r.type],
      title: r.title,
      sub: r.subtitle,
      icon:
        r.type === 'task' ? <CheckSquare size={18} /> :
        r.type === 'workspace' ? <FolderClosed size={18} style={{ color: wsColor(r.color) }} /> :
        r.type === 'member' ? <Avatar id={r.id} name={r.title} size={24} /> :
        r.type === 'page' ? <FileText size={18} /> :
        r.type === 'crm_company' ? <Building2 size={18} /> :
        r.type === 'crm_contact' ? <Contact size={18} /> :
        r.type === 'deal' ? <Handshake size={18} /> :
        <LifeBuoy size={18} />,
      run:
        r.type === 'task' ? () => { onClose(); openTask(r.id); } :
        r.type === 'workspace' ? go(`${base}/w/${r.subtitle.split('· ')[1] ?? ''}`) :
        r.type === 'member' ? go(`${base}/settings/members`) :
        go(`${base}/w/${r.workspaceKey}?tab=${r.tab}`),
    }));
    return [...results, ...actions];
  }, [q, search.data, workspaces.data, base, navigate, onClose, openQuickAdd, openTask]);

  useEffect(() => setActive(0), [q]);

  let lastGroup = '';
  return (
    <Sheet open={open} onClose={onClose} variant="center" full={false} labelledBy="palette-title">
      <h2 id="palette-title" className="sr-only">Cerca</h2>
      <div className="palette-input">
        <Search size={20} color="var(--label-3)" />
        <input
          data-autofocus
          placeholder="Cerca task, spazi, persone…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Cerca"
          aria-controls="palette-list"
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(items.length - 1, a + 1)); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
            if (e.key === 'Enter') { e.preventDefault(); items[active]?.run(); }
          }}
        />
        {search.isFetching && <Spinner />}
      </div>
      <div className="palette-results" id="palette-list" role="listbox">
        {q.trim().length >= 2 && search.data?.length === 0 && !search.isFetching && (
          <p className="hint" style={{ padding: '12px 8px' }}>Nessun risultato per “{q.trim()}”.</p>
        )}
        {items.map((item, i) => {
          const header = item.group !== lastGroup ? <div className="palette-group">{item.group}</div> : null;
          lastGroup = item.group;
          return (
            <div key={item.id}>
              {header}
              <button
                type="button"
                role="option"
                aria-selected={i === active}
                className="palette-item"
                onMouseEnter={() => setActive(i)}
                onClick={item.run}
              >
                <span style={{ width: 24, display: 'grid', placeItems: 'center', color: 'var(--label-2)' }}>{item.icon}</span>
                <span className="grow">
                  <div>{item.title}</div>
                  {item.sub && <div className="sub">{item.sub}</div>}
                </span>
                {item.group === 'Persone' && <User size={14} color="var(--label-3)" />}
              </button>
            </div>
          );
        })}
      </div>
    </Sheet>
  );
}
